import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Plus,
  Search,
  FileText,
  Sparkles,
  Eye,
  Trash2,
  Clock,
  Edit,
  Copy,
  MoreVertical,
  Filter,
  Target,
  Zap,
  BookOpen,
  MessageSquare,
  Mail,
  Layout,
  Tag,
  Type,
  RefreshCw,
  Grid3X3,
  List,
  CheckCircle,
  AlertCircle,
  BarChart3,
  Rocket
} from 'lucide-react';
import { contentService } from '../../services/contentService';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Select from '../../components/ui/Select';
import { Content, ContentType, ContentTone } from '../../types';
import BoostPostModal from '../../components/social/BoostPostModal';

// Mock data for demo
const MOCK_CONTENTS: Content[] = [
  {
    id: '1',
    user_id: '1',
    title: '10 Estratégias de Marketing Digital para 2025',
    slug: '10-estrategias-marketing-digital-2025',
    type: 'blog_post',
    status: 'published',
    excerpt: 'Descubra as principais tendências e estratégias que vão dominar o marketing digital no próximo ano.',
    content: '',
    keywords: ['marketing digital', 'estratégias', 'tendências 2025'],
    target_audience: 'Profissionais de marketing',
    tone: 'professional',
    language: 'pt',
    word_count: 1850,
    reading_time: 8,
    seo_score: 92,
    readability_score: 85,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: '2',
    user_id: '1',
    title: 'Black Friday - Ofertas Imperdíveis',
    slug: 'black-friday-ofertas',
    type: 'ad_copy',
    status: 'approved',
    excerpt: 'Não perca! Descontos de até 70% em toda a loja. Apenas hoje!',
    content: '',
    keywords: ['black friday', 'ofertas', 'descontos'],
    target_audience: 'Consumidores gerais',
    tone: 'persuasive',
    language: 'pt',
    word_count: 45,
    reading_time: 1,
    seo_score: 78,
    readability_score: 95,
    created_at: new Date(Date.now() - 86400000).toISOString(),
    updated_at: new Date(Date.now() - 86400000).toISOString()
  },
  {
    id: '3',
    user_id: '1',
    title: 'Newsletter Semanal - Novidades do Setor',
    slug: 'newsletter-semanal',
    type: 'email',
    status: 'draft',
    excerpt: 'Confira as principais notícias e atualizações da semana no mundo do marketing.',
    content: '',
    keywords: ['newsletter', 'novidades', 'marketing'],
    target_audience: 'Assinantes',
    tone: 'friendly',
    language: 'pt',
    word_count: 520,
    reading_time: 3,
    seo_score: 0,
    readability_score: 88,
    created_at: new Date(Date.now() - 172800000).toISOString(),
    updated_at: new Date(Date.now() - 172800000).toISOString()
  },
  {
    id: '4',
    user_id: '1',
    title: 'Lançamento Produto Premium - Landing Page',
    slug: 'lancamento-produto-premium',
    type: 'landing_page',
    status: 'edited',
    excerpt: 'Conheça o novo Produto Premium: mais recursos, mais performance, mais resultados.',
    content: '',
    keywords: ['produto', 'lançamento', 'premium'],
    target_audience: 'Empresas B2B',
    tone: 'professional',
    language: 'pt',
    word_count: 780,
    reading_time: 4,
    seo_score: 85,
    readability_score: 82,
    created_at: new Date(Date.now() - 259200000).toISOString(),
    updated_at: new Date(Date.now() - 259200000).toISOString()
  },
  {
    id: '5',
    user_id: '1',
    title: 'Como Aumentar suas Vendas Online em 30 Dias',
    slug: 'aumentar-vendas-online-30-dias',
    type: 'blog_post',
    status: 'generated',
    excerpt: 'Um guia prático com dicas comprovadas para impulsionar suas vendas no e-commerce.',
    content: '',
    keywords: ['vendas online', 'e-commerce', 'dicas'],
    target_audience: 'Donos de e-commerce',
    tone: 'informative',
    language: 'pt',
    word_count: 2100,
    reading_time: 9,
    seo_score: 88,
    readability_score: 79,
    created_at: new Date(Date.now() - 345600000).toISOString(),
    updated_at: new Date(Date.now() - 345600000).toISOString()
  },
  {
    id: '6',
    user_id: '1',
    title: 'Post Instagram - Dica do Dia',
    slug: 'post-instagram-dica',
    type: 'social_post',
    status: 'published',
    excerpt: '💡 Dica do Dia: Você sabia que vídeos curtos têm 2x mais engajamento?',
    content: '',
    keywords: ['instagram', 'dica', 'engajamento'],
    target_audience: 'Seguidores Instagram',
    tone: 'casual',
    language: 'pt',
    word_count: 120,
    reading_time: 1,
    seo_score: 0,
    readability_score: 98,
    created_at: new Date(Date.now() - 432000000).toISOString(),
    updated_at: new Date(Date.now() - 432000000).toISOString()
  }
];

const CONTENT_TYPES = [
  { value: '', label: 'All types' },
  { value: 'blog_post', label: 'Blog Post', icon: BookOpen },
  { value: 'social_post', label: 'Social Post', icon: MessageSquare },
  { value: 'ad_copy', label: 'Ad Copy', icon: Target },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'landing_page', label: 'Landing Page', icon: Layout },
  { value: 'product_description', label: 'Product Description', icon: Tag },
  { value: 'meta_description', label: 'Meta Description', icon: FileText },
  { value: 'headline', label: 'Headline', icon: Type }
];

const QUICK_TEMPLATES = [
  { type: 'blog_post', label: 'Blog Post', description: 'SEO-optimized article', icon: BookOpen, color: 'blue' },
  { type: 'social_post', label: 'Social Post', description: 'For Instagram, LinkedIn, etc', icon: MessageSquare, color: 'pink' },
  { type: 'ad_copy', label: 'Ad Copy', description: 'Persuasive copy for ads', icon: Target, color: 'orange' },
  { type: 'email', label: 'Email', description: 'Newsletter or campaign', icon: Mail, color: 'green' }
];

const ContentPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [contents, setContents] = useState<Content[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [boostingContent, setBoostingContent] = useState<Content | null>(null);
  const [showBoostModal, setShowBoostModal] = useState(false);

  useEffect(() => {
    loadContents();
  }, [page, typeFilter, statusFilter]);

  const loadContents = async () => {
    setIsLoading(true);
    try {
      const response = await contentService.getContents({
        page,
        limit: 12,
        search: search || undefined,
        type: (typeFilter || undefined) as ContentType | undefined,
        status: statusFilter || undefined
      });
      if (response.success && response.data?.length > 0) {
        setContents(response.data);
        setTotalPages(response.pagination?.totalPages || 1);
      } else {
        // Use mock data for demo
        let filtered = [...MOCK_CONTENTS];
        if (typeFilter) filtered = filtered.filter(c => c.type === typeFilter);
        if (statusFilter) filtered = filtered.filter(c => c.status === statusFilter);
        if (search) filtered = filtered.filter(c =>
          c.title.toLowerCase().includes(search.toLowerCase()) ||
          c.excerpt?.toLowerCase().includes(search.toLowerCase())
        );
        setContents(filtered);
        setTotalPages(1);
      }
    } catch (error) {
      // Use mock data on error
      setContents(MOCK_CONTENTS);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadContents();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    try {
      await contentService.deleteContent(id);
      toast.success('Content deleted');
      loadContents();
    } catch (error) {
      setContents(prev => prev.filter(c => c.id !== id));
      toast.success('Content deleted');
    }
    setOpenMenu(null);
  };

  const handleDuplicate = (content: Content) => {
    const duplicated: Content = {
      ...content,
      id: Date.now().toString(),
      title: `${content.title} (Copy)`,
      status: 'draft',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    setContents(prev => [duplicated, ...prev]);
    toast.success('Content duplicated');
    setOpenMenu(null);
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { variant: 'success' | 'warning' | 'danger' | 'gray' | 'primary'; label: string }> = {
      draft: { variant: 'gray', label: 'Draft' },
      generated: { variant: 'warning', label: 'Generated' },
      edited: { variant: 'primary', label: 'Edited' },
      approved: { variant: 'success', label: 'Approved' },
      published: { variant: 'success', label: 'Published' },
      archived: { variant: 'gray', label: 'Archived' }
    };
    return config[status] || { variant: 'gray', label: status };
  };

  const getTypeConfig = (type: ContentType) => {
    const config: Record<string, { icon: React.ElementType; label: string; color: string }> = {
      blog_post: { icon: BookOpen, label: 'Blog Post', color: 'blue' },
      social_post: { icon: MessageSquare, label: 'Social Post', color: 'pink' },
      ad_copy: { icon: Target, label: 'Ad Copy', color: 'orange' },
      email: { icon: Mail, label: 'Email', color: 'green' },
      landing_page: { icon: Layout, label: 'Landing Page', color: 'purple' },
      product_description: { icon: Tag, label: 'Product', color: 'cyan' },
      meta_description: { icon: FileText, label: 'Meta', color: 'gray' },
      headline: { icon: Type, label: 'Headline', color: 'indigo' }
    };
    return config[type] || { icon: FileText, label: type, color: 'gray' };
  };

  const getSEOScoreColor = (score?: number) => {
    if (!score) return 'text-gray-400';
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Calculate stats
  const stats = {
    total: contents.length,
    published: contents.filter(c => c.status === 'published').length,
    avgSeoScore: Math.round(contents.filter(c => c.seo_score).reduce((sum, c) => sum + (c.seo_score || 0), 0) / (contents.filter(c => c.seo_score).length || 1)),
    totalWords: contents.reduce((sum, c) => sum + (c.word_count || 0), 0)
  };

  const statusOptions = [
    { value: '', label: 'All statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'generated', label: 'Generated' },
    { value: 'edited', label: 'Edited' },
    { value: 'approved', label: 'Approved' },
    { value: 'published', label: 'Published' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Content Generator</h1>
          <p className="text-gray-600 dark:text-gray-400">Create optimized content with artificial intelligence</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="secondary" size="sm" onClick={() => loadContents()} leftIcon={<RefreshCw className="w-4 h-4" />}>
            Refresh
          </Button>
          <Link to="/content/generate">
            <Button leftIcon={<Sparkles className="w-4 h-4" />}>
              Generate Content
            </Button>
          </Link>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Total Content</p>
                <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-800 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600 dark:text-green-400">Published</p>
                <p className="text-2xl font-bold text-green-700 dark:text-green-300">{stats.published}</p>
                <p className="text-xs text-green-600 dark:text-green-400 flex items-center mt-1">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {Math.round((stats.published / (stats.total || 1)) * 100)}% of total
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 dark:bg-green-800 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Average SEO Score</p>
                <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{stats.avgSeoScore}</p>
                <p className="text-xs text-purple-600 dark:text-purple-400 flex items-center mt-1">
                  <BarChart3 className="w-3 h-3 mr-1" />
                  {stats.avgSeoScore >= 80 ? 'Excellent!' : stats.avgSeoScore >= 60 ? 'Good' : 'Can improve'}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-800 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border-orange-200 dark:border-orange-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600 dark:text-orange-400">Words Generated</p>
                <p className="text-2xl font-bold text-orange-700 dark:text-orange-300">{stats.totalWords.toLocaleString()}</p>
                <p className="text-xs text-orange-600 dark:text-orange-400 flex items-center mt-1">
                  <Zap className="w-3 h-3 mr-1" />
                  Saved ~{Math.round(stats.totalWords / 300)}h of writing
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-800 rounded-xl flex items-center justify-center">
                <Type className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Templates */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        {QUICK_TEMPLATES.map((template) => {
          const Icon = template.icon;
          return (
            <Link key={template.type} to={`/content/generate?type=${template.type}`}>
              <Card className="hover:shadow-md transition-all cursor-pointer group hover:border-primary-300 dark:hover:border-primary-700">
                <CardContent className="pt-6 text-center">
                  <div className={`w-12 h-12 mx-auto mb-3 bg-${template.color}-100 dark:bg-${template.color}-900/30 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-6 h-6 text-${template.color}-600 dark:text-${template.color}-400`} />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{template.label}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{template.description}</p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2 max-w-md">
              <Input
                placeholder="Search content..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftIcon={<Search className="w-5 h-5" />}
                className="flex-1"
              />
              <Button type="submit" variant="secondary">
                Search
              </Button>
            </form>

            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-full sm:w-40">
                <Select
                  options={CONTENT_TYPES}
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <div className="w-full sm:w-40">
                <Select
                  options={statusOptions}
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <div className="flex border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/30' : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/30' : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content Grid/List */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-48 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : contents.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No content found</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">Start by generating your first AI content.</p>
              <Link to="/content/generate">
                <Button leftIcon={<Sparkles className="w-4 h-4" />}>
                  Generate First Content
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {contents.map((content) => {
            const typeConfig = getTypeConfig(content.type);
            const statusConfig = getStatusBadge(content.status);
            const TypeIcon = typeConfig.icon;

            return (
              <Card key={content.id} className="hover:shadow-lg transition-all group">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 bg-${typeConfig.color}-100 dark:bg-${typeConfig.color}-900/30 rounded-lg flex items-center justify-center`}>
                        <TypeIcon className={`w-4 h-4 text-${typeConfig.color}-600 dark:text-${typeConfig.color}-400`} />
                      </div>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{typeConfig.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>
                      <div className="relative">
                        <button
                          onClick={() => setOpenMenu(openMenu === content.id ? null : content.id)}
                          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <MoreVertical className="w-4 h-4 text-gray-500" />
                        </button>
                        {openMenu === content.id && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setOpenMenu(null)} />
                            <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 py-1">
                              <button className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full">
                                <Eye className="w-4 h-4" />
                                View
                              </button>
                              <button className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full">
                                <Edit className="w-4 h-4" />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDuplicate(content)}
                                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full"
                              >
                                <Copy className="w-4 h-4" />
                                Duplicate
                              </button>
                              {(content.type === 'social_post' || content.type === 'ad_copy') && (
                                <button
                                  onClick={() => { setBoostingContent(content); setShowBoostModal(true); setOpenMenu(null); }}
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 w-full"
                                >
                                  <Rocket className="w-4 h-4" />
                                  Impulsionar
                                </button>
                              )}
                              <hr className="my-1 border-gray-200 dark:border-gray-700" />
                              <button
                                onClick={() => handleDelete(content.id)}
                                className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 w-full"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    {content.title}
                  </h3>

                  {content.excerpt && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-4">
                      {content.excerpt}
                    </p>
                  )}

                  <div className="flex items-center justify-between text-sm mb-4">
                    <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <FileText className="w-3.5 h-3.5" />
                        {content.word_count}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {content.reading_time}min
                      </span>
                    </div>
                    {content.seo_score !== undefined && content.seo_score > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">SEO</span>
                        <span className={`font-bold ${getSEOScoreColor(content.seo_score)}`}>
                          {content.seo_score}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" className="flex-1">
                      <Eye className="w-4 h-4 mr-2" />
                      View
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Edit className="w-4 h-4" />
                    </Button>
                    {(content.type === 'social_post' || content.type === 'ad_copy') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setBoostingContent(content); setShowBoostModal(true); }}
                        className="text-primary-600 hover:text-primary-700"
                        title="Impulsionar na Meta"
                      >
                        <Rocket className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        /* List View */
        <Card padding={false}>
          <div className="overflow-x-auto scrollbar-hide">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Content
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Words
                  </th>
                  <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    SEO
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {contents.map((content) => {
                  const typeConfig = getTypeConfig(content.type);
                  const statusConfig = getStatusBadge(content.status);
                  const TypeIcon = typeConfig.icon;

                  return (
                    <tr key={content.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                      <td className="px-3 sm:px-6 py-2 sm:py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 bg-${typeConfig.color}-100 dark:bg-${typeConfig.color}-900/30 rounded-lg flex items-center justify-center flex-shrink-0`}>
                            <TypeIcon className={`w-5 h-5 text-${typeConfig.color}-600 dark:text-${typeConfig.color}-400`} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white line-clamp-1">{content.title}</p>
                            {content.excerpt && (
                              <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">{content.excerpt}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{typeConfig.label}</span>
                      </td>
                      <td className="px-4 py-4">
                        <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{content.word_count}</span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        {content.seo_score ? (
                          <span className={`font-bold ${getSEOScoreColor(content.seo_score)}`}>
                            {content.seo_score}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(content.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Edit className="w-4 h-4" />
                          </Button>
                          {(content.type === 'social_post' || content.type === 'ad_copy') && (
                            <button
                              onClick={() => { setBoostingContent(content); setShowBoostModal(true); }}
                              className="p-2 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg text-primary-600"
                              title="Impulsionar na Meta"
                            >
                              <Rocket className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(content.id)}
                            className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      {/* AI Insights Banner */}
      <Card className="bg-gradient-to-r from-primary-600 to-purple-600 text-white border-0">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold">AI Tip</h3>
                <p className="text-sm opacity-90">
                  Your content with more than 1500 words is performing 3x better in SEO. Consider creating more long-form articles!
                </p>
              </div>
            </div>
            <Link to="/content/generate?type=blog_post">
              <Button variant="secondary" className="bg-white text-primary-600 hover:bg-gray-100">
                Create article
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
      {/* Boost Modal */}
      <BoostPostModal
        isOpen={showBoostModal}
        onClose={() => { setShowBoostModal(false); setBoostingContent(null); }}
        contentItem={boostingContent}
        onBoostSuccess={() => { setShowBoostModal(false); setBoostingContent(null); }}
      />
    </div>
  );
};

export default ContentPage;
